import React from "react";
import { useTranslation } from "react-i18next";

export default function CertificateInfoModal({
  isOpen,
  onClose,
  certificateData,
}) {
  const { t } = useTranslation();

  if (!isOpen || !certificateData) return null;

  const certificateTypeMap = {
    employmentCertificate: t("admin.certificate.employment") || "재직증명서",
    enrollmentCertificate: t("admin.certificate.enrollment") || "재학증명서",
    admissionCertificate: t("admin.certificate.admission") || "입학증명서",
    expectedGraduationCertificate:
      t("admin.certificate.expectedGraduation") || "졸업예정증명서",
    graduationCertificate: t("admin.certificate.graduation") || "졸업증명서",
  };

  const statusMap = {
    pending: t("admin.certificate.pending") || "대기",
    completed: t("admin.certificate.completed") || "완료",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR");
  };

  const renderBasicInfo = () => (
    <div className="mb-3" style={{ fontSize: "15px" }}>
      <h5 className="border-bottom pb-2 mb-3">
        {t("admin.certificate.basicInfo") || "기본 정보"}
      </h5>
      <div className="row">
        <div className="col-md-6">
          <table className="table table-sm table-borderless">
            <tbody>
              <tr>
                <td className="fw-bold" style={{ width: "120px" }}>
                  {t("admin.certificate.certificateNo") || "발급번호"}:
                </td>
                <td>{certificateData.certificate_no || "-"}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.certificateType") || "증명서 유형"}:
                </td>
                <td>
                  {certificateTypeMap[certificateData.certificate_type] ||
                    certificateData.certificate_type}
                </td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.requestDate") || "요청일"}:
                </td>
                <td>{formatDate(certificateData.request_date)}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.issueDate") || "발급일"}:
                </td>
                <td>{formatDate(certificateData.issue_date)}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.status") || "상태"}:
                </td>
                <td>
                  <span
                    className={`badge ${
                      certificateData.status === "completed"
                        ? "bg-success"
                        : "bg-warning"
                    }`}
                  >
                    {statusMap[certificateData.status] ||
                      certificateData.status}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <table className="table table-sm table-borderless">
            <tbody>
              <tr>
                <td className="fw-bold" style={{ width: "120px" }}>
                  {t("admin.certificate.subject") || "대상자"}:
                </td>
                <td>{certificateData.subjectUser?.fullname || "-"}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.subjectId") || "대상자 ID"}:
                </td>
                <td>{certificateData.subject_username || "-"}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.issuer") || "발급자"}:
                </td>
                <td>{certificateData.issuerUser?.fullname || "-"}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.issuerId") || "발급자 ID"}:
                </td>
                <td>{certificateData.issuer || "-"}</td>
              </tr>
              <tr>
                <td className="fw-bold">
                  {t("admin.certificate.purpose") || "용도"}:
                </td>
                <td>
                  {certificateData.purpose ||
                    certificateData.snapshot?.purpose ||
                    "-"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSnapshotInfo = () => {
    if (!certificateData.snapshot) return null;

    const snapshot = certificateData.snapshot;

    return (
      <div className="mb-0" style={{ fontSize: "15px" }}>
        <h5 className="border-bottom pb-2 mb-3">
          {t("admin.certificate.snapshotInfo") || "발급 당시 정보"}
        </h5>
        <div className="row">
          <div className="col-md-6">
            <table className="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td className="fw-bold" style={{ width: "120px" }}>
                    {t("admin.certificate.fullname") || "성명"}:
                  </td>
                  <td>{snapshot.fullname || "-"}</td>
                </tr>
                <tr>
                  <td className="fw-bold">
                    {t("admin.certificate.birthday") || "생년월일"}:
                  </td>
                  <td>{snapshot.birthday || "-"}</td>
                </tr>
                <tr>
                  <td className="fw-bold">
                    {t("admin.certificate.address") || "주소"}:
                  </td>
                  <td>{snapshot.address || "-"}</td>
                </tr>
                {snapshot.position && (
                  <tr>
                    <td className="fw-bold">
                      {t("admin.certificate.position") || "직위"}:
                    </td>
                    <td>{snapshot.position}</td>
                  </tr>
                )}
                {snapshot.entranceDate && (
                  <tr>
                    <td className="fw-bold">
                      {t("admin.certificate.entranceDate") || "입사일"}:
                    </td>
                    <td>{snapshot.entranceDate}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="col-md-6">
            <table className="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td className="fw-bold" style={{ width: "120px" }}>
                    {t("admin.certificate.schoolName") || "학교명"}:
                  </td>
                  <td>{snapshot.schoolName || "-"}</td>
                </tr>
                <tr>
                  <td className="fw-bold">
                    {t("admin.certificate.schoolNameEng") || "학교명(영문)"}:
                  </td>
                  <td>{snapshot.schoolNameEnglish || "-"}</td>
                </tr>
                <tr>
                  <td className="fw-bold">
                    {t("admin.certificate.schoolAddress") || "학교주소"}:
                  </td>
                  <td>
                    {snapshot.schoolAddress1 && (
                      <div>{snapshot.schoolAddress1}</div>
                    )}
                    {snapshot.schoolAddress2 && (
                      <div>{snapshot.schoolAddress2}</div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="fw-bold">
                    {t("admin.certificate.schoolAddressEng") ||
                      "학교주소(영문)"}
                    :
                  </td>
                  <td>
                    {snapshot.schoolAddressEnglish1 && (
                      <div>{snapshot.schoolAddressEnglish1}</div>
                    )}
                    {snapshot.schoolAddressEnglish2 && (
                      <div>{snapshot.schoolAddressEnglish2}</div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-container">
      <div
        className="modalForUnit"
        style={{
          padding: "20px",
          maxWidth: "1100px",
          width: "90vw",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div className="modal-header d-flex justify-content-between align-items-center mb-3">
          <h3 style={{ fontSize: "1.25rem", fontWeight: "bold" }}>
            {t("admin.certificate.detailTitle") || "증명서 발급 정보 상세"}
          </h3>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            style={{
              width: "20px",
              height: "20px",
              padding: "0",
              marginTop: "-8px",
            }}
          ></button>
        </div>

        <div className="modal-content mb-3">
          {renderBasicInfo()}
          {renderSnapshotInfo()}
        </div>

        <div className="modal-footer d-flex justify-content-end mt-1">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("common.labels.close") || "닫기"}
          </button>
        </div>
      </div>
    </div>
  );
}
